'use client';

import {
  useState, useEffect, useCallback, useRef,
} from 'react';
import {
  MC_CONTEXT_COOKIE,
  clearMcContextCookieClient,
  isMcContextCookieValue,
  readMcContextCookieClient,
  writeMcContextCookieClient,
} from '@utils/mc-context-cookie/mcContextCookie';

const MC_CONTEXT_EVENT = 'mc-context-change';

export type MissionControlContext = 'candidate' | 'company';

interface UseMissionControlContextOptions {
  /**
   * Server-derived initial context for first render — the server picks it from the
   * `mc_context` cookie (explicit user preference), defaulting to `fallbackContext` when absent.
   * Eliminates the candidate↔company flash.
   */
  initialContext?: MissionControlContext;
  /**
   * Context to use when there is NO explicit preference (no `mc_context` cookie, no legacy
   * localStorage value). Defaults to `candidate`. Callers that know the visitor is a company
   * account (authenticated + `hasBusinessWallet`) pass `'company'` so a wallet holder with no
   * saved preference lands on the company view ("see my jobs"). An explicit cookie always wins.
   */
  fallbackContext?: MissionControlContext;
}

interface UseMissionControlContextReturn {
  currentContext: MissionControlContext;
  switchContext: () => void;
  setContext: (nextContext: MissionControlContext) => void;
  clearContext: () => void;
}

/**
 * @description Hook for managing Mission Control context (candidate vs company view).
 *
 * Priority order:
 * 1. `mc_context` cookie (set only by an explicit user switch) — read server-side into
 *    `initialContext` for the SSR HTML, and re-read on hydration (also migrates a legacy
 *    localStorage value).
 * 2. Fallback: `fallbackContext` (default 'candidate'). Callers that know the visitor is a
 *    company account (authenticated + `hasBusinessWallet`) pass `'company'`, so a wallet
 *    holder with no saved preference defaults to the company view. An explicit cookie wins.
 */
export const useMissionControlContext = (
  { initialContext, fallbackContext = 'candidate' }: UseMissionControlContextOptions = {},
): UseMissionControlContextReturn => {
  // Only explicit user actions should persist and override the candidate default.
  const hasExplicitPreferenceRef = useRef(false);

  const dispatchContextChange = useCallback((nextContext: MissionControlContext) => {
    if (typeof window === 'undefined') return;

    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent<MissionControlContext>(MC_CONTEXT_EVENT, { detail: nextContext }));
    }, 0);
  }, []);

  const persistExplicitPreference = useCallback((nextContext: MissionControlContext) => {
    hasExplicitPreferenceRef.current = true;
    writeMcContextCookieClient(nextContext);
  }, []);

  // First render uses the server-supplied initialContext (cookie-derived) so SSR and client agree.
  const [currentContext, setCurrentContext] = useState<MissionControlContext>(initialContext ?? fallbackContext);

  // After hydration, apply explicit cookie preference (user has manually switched before).
  // The server page reads the same cookie and seeds initialContext, so this is normally a no-op;
  // it still runs to migrate legacy localStorage values left over from before the cookie switch.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const cookieValue = readMcContextCookieClient();
    if (cookieValue) {
      hasExplicitPreferenceRef.current = true;
      setCurrentContext(cookieValue);
      return;
    }

    try {
      const legacy = localStorage.getItem(MC_CONTEXT_COOKIE);
      if (isMcContextCookieValue(legacy)) {
        hasExplicitPreferenceRef.current = true;
        writeMcContextCookieClient(legacy);
        localStorage.removeItem(MC_CONTEXT_COOKIE);
        setCurrentContext(legacy);
      }
    } catch {
      // ignore storage read failures and fall back to derived values
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (hasExplicitPreferenceRef.current) return;

    // No saved preference → use the server-derived initialContext, falling back to the
    // caller-supplied fallbackContext (company for wallet holders, else candidate).
    setCurrentContext(initialContext ?? fallbackContext);
  }, [initialContext, fallbackContext]);

  // Listen for context changes dispatched by other hook instances (e.g. header vs MissionControl).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleExternalSwitch = (event: Event) => {
      const nextContext = (event as CustomEvent<MissionControlContext>).detail;
      if (nextContext === 'candidate' || nextContext === 'company') {
        setCurrentContext(nextContext);
      }
    };

    window.addEventListener(MC_CONTEXT_EVENT, handleExternalSwitch);
    return () => window.removeEventListener(MC_CONTEXT_EVENT, handleExternalSwitch);
  }, []);

  const setContext = useCallback((nextContext: MissionControlContext) => {
    persistExplicitPreference(nextContext);
    setCurrentContext(nextContext);
    dispatchContextChange(nextContext);
  }, [dispatchContextChange, persistExplicitPreference]);

  const switchContext = useCallback(() => {
    setCurrentContext((prevContext) => {
      const nextContext: MissionControlContext = prevContext === 'candidate' ? 'company' : 'candidate';

      persistExplicitPreference(nextContext);
      dispatchContextChange(nextContext);

      return nextContext;
    });
  }, [dispatchContextChange, persistExplicitPreference]);

  const clearContext = useCallback(() => {
    hasExplicitPreferenceRef.current = false;
    setCurrentContext('candidate');
    dispatchContextChange('candidate');
    clearMcContextCookieClient();
  }, [dispatchContextChange]);

  return {
    currentContext,
    switchContext,
    setContext,
    clearContext,
  };
};
